import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Marker, type MapRef } from '@vis.gl/react-maplibre';
import MapView from 'components/Map/MapView';
import { type Truck, type SiteZone, type TrafficEvent } from './types';
import { decodeGeohash } from 'utils/geohash';
import MapLegend from 'components/Map/MapLegend';
import { images } from 'components/Asset/PNG';
import {
  mapVehiclesAssets,
  getImageNameByMakeModelAndAsset,
} from 'utils/helpers/mapAssetImage';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';

const STATUS_COLORS: Record<string, string> = {
  active: '#4ade80',
  idle: '#fbbf24',
  down: '#f87171',
};

const SITE_FILL_COLORS: Record<string, string> = {
  loading: '#4ade80',
  dumping: '#fbbf24',
  waiting: '#FF8C00',
  fuel: '#32CD32',
};

function buildIdLabel(
  bumperNumber: string,
  status: string,
  ignitionOn: boolean
): string {
  const canvas = document.createElement('canvas');
  canvas.width = 150;
  canvas.height = 34;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const borderColor = STATUS_COLORS[status] ?? '#fff';
  const dotColor = ignitionOn ? '#4ade80' : '#f87171';

  ctx.fillStyle =
    status === 'active' ? 'rgba(10,15,30,0.88)' : 'rgba(10,15,30,0.65)';
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(canvas.width - r, 0);
  ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
  ctx.lineTo(canvas.width, canvas.height - r);
  ctx.quadraticCurveTo(
    canvas.width,
    canvas.height,
    canvas.width - r,
    canvas.height
  );
  ctx.lineTo(r, canvas.height);
  ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = dotColor;
  ctx.beginPath();
  ctx.arc(11, canvas.height / 2, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(bumperNumber, 20, canvas.height / 2);

  return canvas.toDataURL();
}

function getAssetPngKey(
  assetType: string,
  make?: string,
  model?: string
): string {
  if (make && model) {
    return getImageNameByMakeModelAndAsset(make, model, assetType, 'Small');
  }
  const svgKey = mapVehiclesAssets(assetType);
  if (
    svgKey === 'haulTruckImage' ||
    svgKey === 'lightTruckImage' ||
    svgKey === 'heavyTruckImage'
  ) {
    return 'fallbackhaul_truckImageSmall';
  }
  return 'fallbackloaderImageSmall';
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

const CLUSTER_EPS_DEGREES = 0.00005; // ~5m — trucks closer than this are treated as colocated
const CLUSTER_FAN_RADIUS_PX = 16;

function computeClusterOffsets(
  trucks: Truck[],
  positions: Record<string, { lat: number; lon: number; alt: number }>
): Record<string, { dx: number; dy: number }> {
  const groups: Array<{ lat: number; lon: number; ids: string[] }> = [];

  trucks.forEach((truck) => {
    const pos = positions[truck.id];
    if (!pos) return;
    const group = groups.find(
      (g) =>
        Math.abs(g.lat - pos.lat) < CLUSTER_EPS_DEGREES &&
        Math.abs(g.lon - pos.lon) < CLUSTER_EPS_DEGREES
    );
    if (group) {
      group.ids.push(truck.id);
    } else {
      groups.push({ lat: pos.lat, lon: pos.lon, ids: [truck.id] });
    }
  });

  const offsets: Record<string, { dx: number; dy: number }> = {};
  groups.forEach((group) => {
    if (group.ids.length === 1) {
      offsets[group.ids[0]] = { dx: 0, dy: 0 };
      return;
    }
    group.ids.forEach((id, index) => {
      const angle = (2 * Math.PI * index) / group.ids.length;
      offsets[id] = {
        dx: Math.cos(angle) * CLUSTER_FAN_RADIUS_PX,
        dy: Math.sin(angle) * CLUSTER_FAN_RADIUS_PX,
      };
    });
  });
  return offsets;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

interface GpsPoint {
  lat: number;
  lon: number;
  alt: number;
}

interface Props {
  trucks: Truck[];
  sites: SiteZone[];
  livePositions: Record<string, GpsPoint>;
  ignitionMap: Record<string, boolean>;
  selectedId: string | null;
  onTruckClick: (id: string) => void;
  onOverride: (id: string) => void;
  enableDarkTheme: boolean;
  trafficEvents: TrafficEvent[];
}

// Duration over which we animate from old → new position (slightly under poll interval)
const TRANSITION_SECS = 8;

const DispatchMap: React.FC<Props> = ({
  trucks,
  sites,
  livePositions,
  ignitionMap,
  selectedId,
  onTruckClick,
  onOverride,
  enableDarkTheme,
  trafficEvents,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { customerCode } = useCustomerInfo();
  const mapRef = useRef<MapRef | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedTrafficEdge, setSelectedTrafficEdge] = useState<string | null>(
    null
  );

  const [displayPositions, setDisplayPositions] = useState<
    Record<string, GpsPoint>
  >({});

  // Animation state per truck: from → to + progress [0..1]
  const animRef = useRef<
    Record<string, { from: GpsPoint; to: GpsPoint; progress: number }>
  >({});
  const animFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  // When livePositions changes, seed initial positions or start a transition
  useEffect(() => {
    const initialSeeds: Record<string, GpsPoint> = {};

    trucks.forEach((t) => {
      const newPos = livePositions[t.id];
      if (!newPos) return;

      if (!displayPositions[t.id] && !animRef.current[t.id]) {
        // First GPS fix for this truck — place it immediately, no animation
        initialSeeds[t.id] = newPos;
        return;
      }

      const current = displayPositions[t.id] ?? newPos;
      const existing = animRef.current[t.id];
      const target = existing?.to ?? current;

      if (
        Math.abs(newPos.lat - target.lat) > 0.000001 ||
        Math.abs(newPos.lon - target.lon) > 0.000001
      ) {
        animRef.current[t.id] = { from: current, to: newPos, progress: 0 };
      }
    });

    if (Object.keys(initialSeeds).length > 0) {
      setDisplayPositions((prev) => ({ ...prev, ...initialSeeds }));
    }
  }, [livePositions]);

  const animate = useCallback((time: number) => {
    if (lastTimeRef.current === null) lastTimeRef.current = time;
    const dt = Math.min((time - lastTimeRef.current) / 1000, 0.1);
    lastTimeRef.current = time;

    let hasActive = false;
    setDisplayPositions((prev) => {
      const next = { ...prev };
      for (const id in animRef.current) {
        const anim = animRef.current[id];
        if (anim.progress >= 1) continue;

        hasActive = true;
        anim.progress = Math.min(anim.progress + dt / TRANSITION_SECS, 1);
        const t = easeInOut(anim.progress);
        next[id] = {
          lat: lerp(anim.from.lat, anim.to.lat, t),
          lon: lerp(anim.from.lon, anim.to.lon, t),
          alt: lerp(anim.from.alt, anim.to.alt, t),
        };
      }
      return hasActive ? next : prev;
    });

    animFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current !== null)
        cancelAnimationFrame(animFrameRef.current);
    };
  }, [animate]);

  // Fly to the site average on mount / whenever the site list changes.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || sites.length === 0) return;
    const avgLon =
      sites.reduce((sum, s) => sum + s.longitude, 0) / sites.length;
    const avgLat = sites.reduce((sum, s) => sum + s.latitude, 0) / sites.length;
    map.jumpTo({ center: [avgLon, avgLat], zoom: 16 });
  }, [sites]);

  const siteMarkers = useMemo(() => {
    return sites
      .filter(
        (site) =>
          Number.isFinite(site.latitude) && Number.isFinite(site.longitude)
      )
      .map((site) => {
        const fillHex = SITE_FILL_COLORS[site.type] ?? '#60a5fa';
        const isSelected = selectedSiteId === site.id;
        const radius =
          Number.isFinite(site.radius) && site.radius > 0 ? site.radius : 30;

        return (
          <Marker
            key={site.id}
            longitude={site.longitude}
            latitude={site.latitude}
          >
            <Box
              onClick={() => {
                setSelectedSiteId((prev) =>
                  prev === site.id ? null : site.id
                );
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 1px 3px rgba(0,0,0,0.9)',
                  mb: '2px',
                  whiteSpace: 'nowrap',
                }}
              >
                {site.name}
              </Typography>
              <Box
                sx={{
                  width: radius,
                  height: radius,
                  borderRadius: '50%',
                  backgroundColor: fillHex,
                  opacity: isSelected ? 0.85 : 0.6,
                  border: `${isSelected ? 3 : 2}px solid ${fillHex}`,
                }}
              />
            </Box>
          </Marker>
        );
      });
  }, [sites, selectedSiteId]);

  const trafficByEdge = useMemo(() => {
    const map: Record<
      string,
      {
        lat: number;
        lon: number;
        devices: string[];
        overCapacity: boolean;
        capacity: number;
        occupancy: number;
      }
    > = {};
    trafficEvents.forEach((ev) => {
      const key = ev.edge_id ?? ev.node_id;
      if (!key || !ev.geohash) return;
      if (!map[key]) {
        const { lat, lon } = decodeGeohash(ev.geohash);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        map[key] = {
          lat,
          lon,
          devices: [],
          overCapacity:
            ev.type === 'node_blocked' ||
            (ev.edge_occupancy ?? 0) > (ev.edge_capacity ?? 0),
          capacity: ev.edge_capacity ?? 0,
          occupancy: ev.edge_occupancy ?? 0,
        };
      }
      if (ev.device) map[key].devices.push(ev.device);
    });
    return map;
  }, [trafficEvents]);

  const trafficMarkers = useMemo(() => {
    return Object.entries(trafficByEdge).map(([edgeId, info]) => {
      const color = info.overCapacity ? '#f87171' : '#fbbf24';
      return (
        <Marker key={edgeId} longitude={info.lon} latitude={info.lat}>
          <Box
            onClick={() => {
              setSelectedTrafficEdge((prev) =>
                prev === edgeId ? null : edgeId
              );
            }}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: color,
              opacity: 0.75,
              border: `2px solid ${color}`,
              cursor: 'pointer',
            }}
          />
        </Marker>
      );
    });
  }, [trafficByEdge]);

  const truckMarkers = useMemo(() => {
    const clusterOffsets = computeClusterOffsets(trucks, displayPositions);
    return trucks.map((truck) => {
      const pos = displayPositions[truck.id];
      if (!pos) return null;
      const isVisible = !selectedId || truck.id === selectedId;
      if (!isVisible) return null;
      const labelImage = buildIdLabel(
        truck.bumperNumber ?? truck.id,
        truck.status,
        ignitionMap[truck.id] ?? false
      );
      const pngImage =
        images[getAssetPngKey(truck.assetType, truck.make, truck.model)];
      const isSelected = selectedId === truck.id;
      const fan = clusterOffsets[truck.id] ?? { dx: 0, dy: 0 };

      return (
        <Marker
          key={truck.id}
          longitude={pos.lon}
          latitude={pos.lat}
          anchor="center"
          offset={[fan.dx, fan.dy]}
        >
          <Box
            onClick={() => {
              onTruckClick(truck.id);
            }}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={labelImage}
              sx={{ height: 17, mb: '2px' }}
            />
            {isSelected && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#6366f1',
                  opacity: 0.3,
                  zIndex: -1,
                }}
              />
            )}
            <Box component="img" src={pngImage} sx={{ height: 28 }} />
          </Box>
        </Marker>
      );
    });
  }, [trucks, displayPositions, selectedId, ignitionMap, onTruckClick]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '10px',
      }}
    >
      <MapView
        ref={mapRef}
        basemap={hasNoSatelliteCoverage(customerCode) ? undefined : 'satellite'}
        maxZoom={19}
      >
        {siteMarkers}
        {truckMarkers}
        {trafficMarkers}
      </MapView>

      <Box sx={{ position: 'absolute', bottom: 12, left: 12, zIndex: 10 }}>
        <MapLegend
          enableDarkTheme={enableDarkTheme}
          showPitAreas={true}
          showSiteAreas={false}
          showTrafficEvents={true}
          fontSize={10}
          pinSize={14}
        />
      </Box>

      {/* Asset detail popup */}
      {(() => {
        const truck = trucks.find((t) => t.id === selectedId);
        if (!truck) return null;
        const statusColor = STATUS_COLORS[truck.status] ?? '#fff';
        const payloadPct =
          truck.payload.capacity > 0
            ? Math.round((truck.payload.current / truck.payload.capacity) * 100)
            : 0;
        const popupBg = enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground;
        const popupBorder = palette.cardBorderColor;
        const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
        const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';
        const statTrip = enableDarkTheme ? '#c9d8f0' : '#424242';
        return (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 20,
              background: popupBg,
              border: `1px solid ${popupBorder}`,
              borderTop: `3px solid ${statusColor}`,
              borderRadius: '10px',
              padding: '14px 16px',
              backdropFilter: 'blur(14px)',
              minWidth: '220px',
              maxWidth: '260px',
              boxShadow: `0 4px 24px rgba(0,0,0,0.3)`,
            }}
          >
            <Box
              onClick={() => {
                onTruckClick(truck.id);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 10,
                fontSize: '14px',
                color: textMuted,
                cursor: 'pointer',
                lineHeight: 1,
                '&:hover': { color: textPrimary },
              }}
            >
              ✕
            </Box>

            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: textPrimary,
                mb: '2px',
                pr: '16px',
              }}
            >
              {truck.bumperNumber ?? truck.id}
              {truck.operator ? ` — ${truck.operator}` : ''}
            </Typography>
            <Typography
              sx={{
                fontSize: '11px',
                color: statusColor,
                mb: '10px',
                fontWeight: 600,
              }}
            >
              {truck.currentActivity}
            </Typography>

            {[
              {
                label: t('dm_map_trip'),
                value: `#${truck.trips.current} / ${truck.trips.target}`,
                color: statTrip,
              },
              {
                label: t('dm_map_payload'),
                value: `${truck.payload.current.toLocaleString()} / ${truck.payload.capacity.toLocaleString()} T (${payloadPct}%)`,
                color: '#4ade80',
              },
              {
                label: t('dm_map_efficiency'),
                value: `${truck.efficiency} T/hr`,
                color: '#a5b4fc',
              },
              {
                label: t('dm_map_fuel'),
                value: truck.fuelLevel !== null ? `${truck.fuelLevel}%` : '–',
                color:
                  truck.fuelLevel !== null && truck.fuelLevel < 20
                    ? '#f87171'
                    : '#fbbf24',
              },
            ].map(({ label, value, color }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '12px',
                  mb: '6px',
                }}
              >
                <Typography
                  sx={{ fontSize: '10px', color: textMuted, flexShrink: 0 }}
                >
                  {label}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color,
                    textAlign: 'right',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}

            <Box
              onClick={() => {
                onOverride(truck.id);
              }}
              sx={{
                mt: '8px',
                bgcolor: 'primary.main',
                color: '#fff',
                borderRadius: '6px',
                padding: '7px 10px',
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                transition: 'opacity 0.15s',
                '&:hover': { opacity: 0.88 },
              }}
            >
              ⚡ {t('dm_map_override_plan')}
            </Box>
          </Box>
        );
      })()}

      {/* Site detail popup */}
      {(() => {
        const site = sites.find((s) => s.id === selectedSiteId);
        if (!site) return null;
        const fillHex = SITE_FILL_COLORS[site.type] ?? '#60a5fa';
        const popupBg = enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground;
        const popupBorder = palette.cardBorderColor;
        const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
        const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';

        const rows =
          site.type === 'loading'
            ? [
                {
                  label: t('dm_map_status'),
                  value: site.isActive
                    ? t('dm_map_active')
                    : t('dm_map_inactive'),
                  color: site.isActive ? '#4ade80' : '#f87171',
                },
                ...(site.grade
                  ? [
                      {
                        label: t('dm_map_grade'),
                        value: site.grade,
                        color: '#a5b4fc',
                      },
                    ]
                  : []),
                ...(site.capacity != null
                  ? [
                      {
                        label: t('dm_map_capacity'),
                        value: `${site.capacity} T`,
                        color: textPrimary,
                      },
                    ]
                  : []),
                ...(site.durationMins != null
                  ? [
                      {
                        label: t('dm_map_load_time'),
                        value: `${site.durationMins} min`,
                        color: textPrimary,
                      },
                    ]
                  : []),
                {
                  label: t('dm_map_lat'),
                  value: site.latitude.toFixed(6),
                  color: textMuted,
                },
                {
                  label: t('dm_map_lon'),
                  value: site.longitude.toFixed(6),
                  color: textMuted,
                },
              ]
            : [
                {
                  label: t('dm_map_status'),
                  value: site.isActive
                    ? t('dm_map_active')
                    : t('dm_map_inactive'),
                  color: site.isActive ? '#4ade80' : '#f87171',
                },
                ...(site.capacity != null
                  ? [
                      {
                        label: t('dm_map_capacity'),
                        value: `${site.capacity} T`,
                        color: textPrimary,
                      },
                    ]
                  : []),
                ...(site.durationMins != null
                  ? [
                      {
                        label: t('dm_map_dump_time'),
                        value: `${site.durationMins.toFixed(1)} min`,
                        color: textPrimary,
                      },
                    ]
                  : []),
                ...(site.blendRatios?.length
                  ? [
                      {
                        label: t('dm_map_blend_ratios'),
                        value: `${site.blendRatios.join(' / ')}%`,
                        color: '#fbbf24',
                      },
                    ]
                  : []),
                {
                  label: t('dm_map_lat'),
                  value: site.latitude.toFixed(6),
                  color: textMuted,
                },
                {
                  label: t('dm_map_lon'),
                  value: site.longitude.toFixed(6),
                  color: textMuted,
                },
              ];

        return (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 20,
              background: popupBg,
              border: `1px solid ${popupBorder}`,
              borderTop: `3px solid ${fillHex}`,
              borderRadius: '10px',
              padding: '14px 16px',
              backdropFilter: 'blur(14px)',
              minWidth: '200px',
              maxWidth: '240px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
            }}
          >
            <Box
              onClick={() => {
                setSelectedSiteId(null);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 10,
                fontSize: '14px',
                color: textMuted,
                cursor: 'pointer',
                lineHeight: 1,
                '&:hover': { color: textPrimary },
              }}
            >
              ✕
            </Box>

            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: textPrimary,
                mb: '10px',
                pr: '16px',
              }}
            >
              {site.name}
            </Typography>

            {rows.map(({ label, value, color }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '12px',
                  mb: '6px',
                }}
              >
                <Typography
                  sx={{ fontSize: '10px', color: textMuted, flexShrink: 0 }}
                >
                  {label}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color,
                    textAlign: 'right',
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* Traffic edge popup */}
      {selectedTrafficEdge &&
        trafficByEdge[selectedTrafficEdge] &&
        (() => {
          const info = trafficByEdge[selectedTrafficEdge];
          const popupBg = enableDarkTheme
            ? ThemePalette.dark.boxBackground
            : ThemePalette.light.boxBackground;
          const popupBorder = enableDarkTheme
            ? ThemePalette.dark.cardBorderColor
            : ThemePalette.light.cardBorderColor;
          const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
          const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';
          return (
            <Box
              sx={{
                position: 'absolute',
                bottom: 60,
                left: 16,
                zIndex: 20,
                background: popupBg,
                border: `1px solid ${popupBorder}`,
                borderTop: '3px solid #f87171',
                borderRadius: '10px',
                padding: '14px 16px',
                backdropFilter: 'blur(14px)',
                minWidth: '200px',
                maxWidth: '260px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
              }}
            >
              <Box
                onClick={() => {
                  setSelectedTrafficEdge(null);
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 10,
                  fontSize: '14px',
                  color: textMuted,
                  cursor: 'pointer',
                  lineHeight: 1,
                  '&:hover': { color: textPrimary },
                }}
              >
                ✕
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: textPrimary,
                  mb: '10px',
                  pr: '16px',
                }}
              >
                {selectedTrafficEdge}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '8px',
                }}
              >
                <Typography sx={{ fontSize: '10px', color: textMuted }}>
                  {t('dm_map_status')}:
                </Typography>
                <Typography
                  sx={{ fontSize: '10px', fontWeight: 600, color: '#f87171' }}
                >
                  {info.occupancy}/{info.capacity}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: '10px', color: textMuted, mb: '4px' }}
              >
                {t('std_asset_haulers')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: textPrimary,
                  wordBreak: 'break-all',
                }}
              >
                {info.devices.join(', ')}
              </Typography>
            </Box>
          );
        })()}
    </Box>
  );
};

export default DispatchMap;
